import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ModalService } from '../../../base/shared/modal/modal.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ProductTemplateDTO, CreateProductTemplateDTO, UpdateProductTemplateDTO, FilterDTO, FilterOperator, SortDirection, UnitType, UnitCategory, DeliveryOption, ProductTemplateImageInputDTO } from '../../../base/models/models.swagger.generated';
import { ProductTemplateFormType } from './product-template-edit-form';
import { PagedQueryModel } from '../../../base/shared/table/paged-query-model';
import { FilterMap } from '../../../base/shared/table/filter-map';
import { FilterMetadata } from 'primeng/api';
import { Subscription } from 'rxjs';
import { ProductTemplateService } from './product-template.service';
import { AppErrorMessagesComponent } from '../../../base/error-messages/app-error-messages.component';
import { SearchFieldComponent } from '../../../base/shared/search-field/search-field.component';
import { CategoryService } from '../category/category.service';
import { ProductTemplateEditDialogComponent } from './product-template-edit-dialog.component';

@Component({
  selector: 'app-product-template',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    ToolbarModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputTextareaModule,
    InputNumberModule,
    MultiSelectModule,
    DropdownModule,
    ToastModule,
    TooltipModule,
    TranslateModule,
    AppErrorMessagesComponent,
    SearchFieldComponent,
    CommonModule,
    ProductTemplateEditDialogComponent
  ],
  templateUrl: './product-template.component.html',
  styleUrls: ['./product-template.component.scss'],
  providers: [ProductTemplateService]
})
export class ProductTemplateComponent implements OnInit, OnDestroy {

  public data: ProductTemplateDTO[] = [];
  public totalNoOfRecords: number = 0;
  public template: ProductTemplateDTO;
  public itemDialog: boolean = false;
  public pagedQuery: PagedQueryModel = PagedQueryModel.getDefault();
  public form: FormGroup<ProductTemplateFormType>;
  public globalFilters = ['id', 'templateName'];
  public columnFilters: { [key: string]: FilterMetadata | FilterMetadata[] } = {};
  public categories: any[] = [];
  public sortOptions = [
    { label: 'Last Modified', value: 'createdDate' },
    { label: 'Name', value: 'templateName' },
    { label: 'Price', value: 'price' }
  ];
  public deliveryOptions = [
    { label: 'Buyer Pickup', value: DeliveryOption.Pickup },
    { label: 'Seller Delivery', value: DeliveryOption.Delivery }
  ];
  public unitTypeOptions: any[] = [];
  public unitCategoryOptions: any[] = [];
  public templateImages: ProductTemplateImageInputDTO[] = [];
  public existingImages: ProductTemplateImageInputDTO[] = [];
  public removeImageIds: number[] = [];
  public setPrimaryImageId: number | null = null;
  public tempGuid: string = '';
  private _subscription: Subscription = new Subscription();
  private searchText: string = '';
  private selectedCategoryId: number | null = null;

  constructor(
    private productTemplateService: ProductTemplateService,
    private categoryService: CategoryService,
    private router: Router,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private modalService: ModalService
  ) { }

  ngOnInit() {
    this.tempGuid = this.generateTempGuid();
    this.initForm(); // Initialize form on component load
    this.loadCategories();
    this.loadEnumOptions();
    this.loadData();
  }

  public get filterMatchModeOptions() {
    return FilterMap.getStringFilterMatchModeOptions();
  }

  private loadCategories() {
    const pagedQuery = PagedQueryModel.getDefault();
    this._subscription.add(
      this.categoryService.getData(pagedQuery).subscribe((data) => {
        this.categories = data.items.map(cat => ({ id: cat.categoryId, name: cat.name }));
      })
    );
  }

  private loadEnumOptions() {
    this.unitTypeOptions = Object.keys(UnitType)
      .filter(k => isNaN(Number(k)))
      .map(k => ({ label: k, value: UnitType[k as any] }));
    
    this.unitCategoryOptions = Object.keys(UnitCategory)
      .filter(k => isNaN(Number(k)))
      .map(k => ({ label: k, value: UnitCategory[k as any] }));
  }

  private loadData() {
    this._subscription.add(
      this.productTemplateService.getData(this.pagedQuery).subscribe((data) => {
        this.data = data.items || [];
        this.totalNoOfRecords = data.totalNumberOfItems || 0;
        this.template = null;
        this.itemDialog = false;
      })
    );
  }

  private getFilters(): FilterDTO[] {
    const filters: FilterDTO[] = [];
    const addFilter = (field: string, operator: FilterOperator, value: any) => {
      filters.push(new FilterDTO({ field, operator, value, isComplex: false }));
    };

    if (this.searchText) {
      addFilter('templateName', FilterOperator.Contains, this.searchText);
    }

    if (this.selectedCategoryId) {
      addFilter('categoryIds', FilterOperator.Contains, this.selectedCategoryId);
    }

    Object.entries(this.columnFilters).forEach(([key, filter]) => {
      if (Array.isArray(filter)) {
        filter.forEach(f => f.value && addFilter(key, FilterMap.getFilterOperator(f.matchMode), f.value));
      } else if (filter?.value) {
        addFilter(key, FilterMap.getFilterOperator(filter.matchMode), filter.value);
      }
    });
    return filters;
  }

  public onFilter(event: any) {
    this.columnFilters = event.filters;
    this.pagedQuery.filters = this.getFilters();
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public onCategoryFilterChange(event: any) {
    this.selectedCategoryId = event.value;
    this.pagedQuery.filters = this.getFilters();
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public onSortFilterChange(event: any) {
    this.pagedQuery.sortBy = event.value;
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public openNew() {
    this.template = null;
    this.templateImages = [];
    this.existingImages = [];
    this.removeImageIds = [];
    this.setPrimaryImageId = null;
    this.tempGuid = this.generateTempGuid();
    this.initForm();
    this.itemDialog = true;
  }

  public editItem(item: ProductTemplateDTO) {
    this._subscription.add(
      this.productTemplateService.getRecord(item.templateId).subscribe((template) => {
        this.template = template;
        this.existingImages = (template.images || []).map(img => new ProductTemplateImageInputDTO(img));
        this.templateImages = [];
        this.removeImageIds = [];
        this.setPrimaryImageId = null;
        this.initForm();
        this.itemDialog = true;
      })
    );
  }

  public deleteTemplate(template: ProductTemplateDTO) {
    const dialog = this.modalService.showOkCancelDialog(
      'i18n.domain.product-template.messages.delete-confirm-title',
      'i18n.domain.product-template.messages.delete-confirm'
    );

    dialog.onOkSubject.subscribe(() => {
      this._subscription.add(
        this.productTemplateService.deleteRecord(template.templateId).subscribe(() => {
          this.loadData();
        })
      );
    });
  }



  public onDialogVisibleChange(visible: boolean) {
    this.itemDialog = visible;
  }

  public onImageSelected(event: any) {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];

      this._subscription.add(
        this.productTemplateService.uploadImage(file, this.tempGuid).subscribe((imageUrl) => {
          
          this.templateImages.push(new ProductTemplateImageInputDTO({
            imageUrl,
            isPrimary: this.templateImages.length === 0,
            sortOrder: this.templateImages.length
          }));
      
          event.target.value = '';
        })
      );
    }
  }

  public onImageError(event: any) {
    console.error('Image failed to load:', event);
  }

  public removeImage(index: number) {
    this.templateImages.splice(index, 1);
    if (this.templateImages.length > 0 && !this.templateImages.some(img => img.isPrimary)) {
      this.templateImages[0].isPrimary = true;
    }
  }

  public removeExistingImage(index: number) {
    const image = this.existingImages[index];
    if (image && (image as any).imageId) {
      this.removeImageIds.push((image as any).imageId);
    }
    this.existingImages.splice(index, 1);
    if (this.existingImages.length > 0 && !this.existingImages.some(img => img.isPrimary)) {
      this.existingImages[0].isPrimary = true;
    }
  }

  public setPrimaryImage(index: number) {
    this.templateImages.forEach((img, i) => {
      img.isPrimary = i === index;
    });
  }

  public setPrimaryExistingImage(index: number) {
    const image = this.existingImages[index];
    this.existingImages.forEach((img, i) => {
      img.isPrimary = i === index;
    });
    if (image && (image as any).imageId) {
      this.setPrimaryImageId = (image as any).imageId;
    }
  }

  public saveTemplate() {
    if (this.form.valid) {
      const templateData = this.form.value;
      if (templateData.Id) {
        const updateDto = new UpdateProductTemplateDTO({
          templateId: templateData.Id,
          templateName: templateData.templateName,
          description: templateData.description,
          price: templateData.price,
          defaultPublishQuantity: templateData.quantity,
          unit: templateData.unit,
          unitValue: templateData.unitValue,
          unitType: templateData.unitType,
          unitCategory: templateData.unitCategory,
          daysToUnlist: templateData.daysToUnlist,
          deliveryOption: templateData.deliveryOption,
          categoryIds: templateData.categoryIds,
          additionalInformation: templateData.additionalInformation,
          addImages: this.templateImages,
          removeImageIds: this.removeImageIds,
          setPrimaryImageId: this.setPrimaryImageId
        });
        this._subscription.add(
          this.productTemplateService.updateRecord(updateDto).subscribe(() => {
            this.loadData();
          })
        );
      } else {
        const createDto = new CreateProductTemplateDTO({
          templateName: templateData.templateName,
          description: templateData.description,
          price: templateData.price,
          defaultPublishQuantity: templateData.quantity,
          unit: templateData.unit,
          unitValue: templateData.unitValue,
          unitType: templateData.unitType,
          unitCategory: templateData.unitCategory,
          daysToUnlist: templateData.daysToUnlist,
          deliveryOption: templateData.deliveryOption,
          categoryIds: templateData.categoryIds,
          additionalInformation: templateData.additionalInformation,
          images: this.templateImages,
          tempGuid: this.tempGuid
        });
        this._subscription.add(
          this.productTemplateService.addRecord(createDto).subscribe(() => {
            this.loadData();
          })
        );
      }
    }
  }

  public onPageChange(event: any) {
    this.pagedQuery.skip = event.first;
    this.pagedQuery.take = event.rows;
    this.loadData();
  }

  public onSearchTextChanged(searchText: string) {
    this.searchText = searchText;
    const filters = this.getFilters();
    this.pagedQuery.filters = filters;
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public onSort(event: any) {
    this.pagedQuery.sortBy = event.field;
    this.pagedQuery.sortDirection = event.order === 1 ? SortDirection.Ascending : SortDirection.Descending;
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public openPublishWizard() {
    this.router.navigate(['/product-template/publish']);
  }

  private initForm() {
    const { templateId, templateName, price, unit, unitValue, unitType, unitCategory, defaultPublishQuantity, daysToUnlist, deliveryOption, additionalInformation, description } = this.template || {};
    const categoryIds = this.template?.categories?.map(c => c.categoryId).filter(id => id != null) || [];

    this.form = this.fb.group<ProductTemplateFormType>({
      Id: this.fb.control<number>(templateId || 0),
      templateName: this.fb.control<string>(templateName || '', [Validators.required,Validators.maxLength(100)]),
      categoryIds: this.fb.control<number[]>(categoryIds, [Validators.required, Validators.minLength(1)]),
      price: this.fb.control<number>(price ? Number(price) : 0, [Validators.required, Validators.min(0.01)]),
      quantity: this.fb.control<number>(defaultPublishQuantity ? Number(defaultPublishQuantity) : 10, [Validators.required, Validators.min(1)]),
      description: this.fb.control<string>(description || '',[Validators.maxLength(500)]),
      deliveryOption: this.fb.control<DeliveryOption>(deliveryOption || DeliveryOption.Pickup),
      daysToUnlist: this.fb.control<number>(daysToUnlist ? Number(daysToUnlist) : 30, Validators.required),
      unit: this.fb.control<string>(unit || ''),
      unitValue: this.fb.control<number>(unitValue ? Number(unitValue) : 1),
      unitType: this.fb.control<UnitType>(unitType),
      unitCategory: this.fb.control<UnitCategory>(unitCategory),
      additionalInformation: this.fb.control<string>(additionalInformation || '')
    });
  }

  private generateTempGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  public onlyIntegers(event: KeyboardEvent) {
    const char = String.fromCharCode(event.which);
    if (!/[0-9]/.test(char)) {
      event.preventDefault();
    }
  }

  public onlyNumbers(event: KeyboardEvent) {
    const char = String.fromCharCode(event.which);
    if (!/[0-9.]/.test(char)) {
      event.preventDefault();
    }
  }


  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }
}
